import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Mail, Edit, Save, X, Eye, Send, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

interface EmailTemplate {
  id: string;
  template_key: string;
  subject: string;
  html_content: string;
  text_content: string;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EmailLog {
  id: string;
  user_id: string;
  template_key: string;
  recipient_email: string;
  subject: string;
  status: string;
  error_message?: string;
  sent_at?: string;
  created_at: string;
  users?: {
    full_name: string;
    email: string;
  };
}

interface Shortcode {
  code: string;
  description: string;
  category: string;
}

export default function EmailTemplateManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'templates' | 'logs'>('templates');
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>({});
  const [showShortcodes, setShowShortcodes] = useState(false);
  const [completeEmailContent, setCompleteEmailContent] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);

  // Available shortcodes for different template types
  const getShortcodes = (templateKey: string): Shortcode[] => {
    const commonShortcodes = [
      { code: '[first_name]', description: 'Customer first name', category: 'Customer Info' },
      { code: '[customer_name]', description: 'Full customer name', category: 'Customer Info' },
      { code: '[email]', description: 'Customer email address', category: 'Customer Info' },
      { code: '[phone]', description: 'Customer phone number', category: 'Customer Info' },
    ];

    if (templateKey === 'track_booking_confirmation') {
      return [
        ...commonShortcodes,
        { code: '[track_type]', description: 'Type of track (Drag Strip/Drift Track)', category: 'Booking Details' },
        { code: '[booking_date]', description: 'Date of booking', category: 'Booking Details' },
        { code: '[booking_time]', description: 'Time of booking', category: 'Booking Details' },
        { code: '[duration]', description: 'Duration in hours', category: 'Booking Details' },
        { code: '[booking_type]', description: 'Type of booking (Individual/Team/Exclusive)', category: 'Booking Details' },
        { code: '[total_amount]', description: 'Total amount to pay', category: 'Payment Info' },
        { code: '[deposit_amount]', description: 'Security deposit amount', category: 'Payment Info' },
        { code: '[special_requests]', description: 'Any special requests', category: 'Booking Details' },
        // Add the booking details template
        { code: '[booking_details_block]', description: 'Complete booking details section with all information', category: 'Template Blocks' },
        { code: '[payment_info_block]', description: 'Complete payment information section', category: 'Template Blocks' },
      ];
    } else if (templateKey === 'event_registration_confirmation') {
      return [
        ...commonShortcodes,
        { code: '[driver_name]', description: 'Name of the driver', category: 'Event Details' },
        { code: '[event_name]', description: 'Name of the event', category: 'Event Details' },
        { code: '[event_date]', description: 'Date of the event', category: 'Event Details' },
        { code: '[registration_deadline]', description: 'Registration deadline', category: 'Event Details' },
        { code: '[total_amount]', description: 'Total registration amount', category: 'Payment Info' },
        // Categories loop shortcode
        { code: '[categories_list]', description: 'Complete list of all registered categories with details', category: 'Categories Loop' },
        // Individual category shortcodes (for single category emails)
        { code: '[category_name]', description: 'Racing category name', category: 'Category Details' },
        { code: '[vehicle_type]', description: 'Vehicle type (car/bike)', category: 'Category Details' },
        { code: '[registration_fee]', description: 'Registration fee amount', category: 'Category Details' },
        { code: '[first_place_prize]', description: 'First place prize amount', category: 'Category Details' },
        { code: '[second_place_prize]', description: 'Second place prize amount', category: 'Category Details' },
        { code: '[wristbands_included]', description: 'Number of wristbands included', category: 'Category Details' },
        { code: '[tickets_included]', description: 'Number of tickets included', category: 'Category Details' },
        { code: '[time_range]', description: 'Time range for the category', category: 'Category Details' },
      ];
    }

    return commonShortcodes;
  };

  useEffect(() => {
    fetchTemplates();
    fetchEmailLogs();
  }, [user]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('template_key');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('email_logs')
        .select(`
          *,
          users(full_name, email)
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setEmailLogs(data || []);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      toast.error('Failed to load email logs');
    }
  };

  const convertTemplateToEditableContent = (template: EmailTemplate) => {
    // Convert the text content back to editable format with shortcodes
    let content = template.text_content || '';
    
    // Convert template variables back to shortcodes for editing
    content = content
      .replace(/{{customer_name}}/g, '[customer_name]')
      .replace(/{{email}}/g, '[email]')
      .replace(/{{phone}}/g, '[phone]')
      .replace(/{{track_type}}/g, '[track_type]')
      .replace(/{{booking_date}}/g, '[booking_date]')
      .replace(/{{booking_time}}/g, '[booking_time]')
      .replace(/{{duration}}/g, '[duration]')
      .replace(/{{booking_type}}/g, '[booking_type]')
      .replace(/{{total_amount}}/g, '[total_amount]')
      .replace(/{{deposit_amount}}/g, '[deposit_amount]')
      .replace(/{{special_requests}}/g, '[special_requests]')
      .replace(/{{driver_name}}/g, '[driver_name]')
      .replace(/{{event_name}}/g, '[event_name]')
      .replace(/{{event_date}}/g, '[event_date]')
      .replace(/{{registration_deadline}}/g, '[registration_deadline]')
      .replace(/{{category_name}}/g, '[category_name]')
      .replace(/{{vehicle_type}}/g, '[vehicle_type]')
      .replace(/{{registration_fee}}/g, '[registration_fee]')
      .replace(/{{first_place_prize}}/g, '[first_place_prize]')
      .replace(/{{second_place_prize}}/g, '[second_place_prize]')
      .replace(/{{wristbands_included}}/g, '[wristbands_included]')
      .replace(/{{tickets_included}}/g, '[tickets_included]')
      .replace(/{{time_range}}/g, '[time_range]')
      // Convert categories loop back to shortcode
      .replace(/{{#each categories}}[\s\S]*?{{\/each}}/g, '[categories_list]');

    return content;
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setCompleteEmailContent(convertTemplateToEditableContent(template));
    setShowShortcodes(false);
  };

  const insertShortcode = (shortcode: string) => {
    const textarea = document.getElementById('complete-email-textarea') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      let insertText = shortcode;
      
      // Special handling for template blocks
      if (shortcode === '[booking_details_block]') {
        insertText = `BOOKING DETAILS:
- Track Type: [track_type]
- Date: [booking_date]
- Time: [booking_time]
- Duration: [duration] hours
- Booking Type: [booking_type]
{{#if traction_compound}}- Traction Compound: Included{{/if}}
{{#if special_requests}}- Special Requests: [special_requests]{{/if}}`;
      } else if (shortcode === '[payment_info_block]') {
        insertText = `PAYMENT INFORMATION:
Total Amount: $[total_amount]
{{#if deposit_amount}}Security Deposit: $[deposit_amount]{{/if}}`;
      } else if (shortcode === '[categories_list]') {
        insertText = `{{#each categories}}
- {{name}} ({{vehicle_type}})
  Registration Fee: Nafl {{registration_fee}}
  Includes: {{wristbands_included}} wristbands, {{tickets_included}} tickets
  {{#if first_place_prize}}1st Place Prize: Nafl {{first_place_prize}}{{/if}}
  {{#if second_place_prize}}2nd Place Prize: Nafl {{second_place_prize}}{{/if}}
  {{#if time_range}}Time Range: {{time_range}}{{/if}}
{{/each}}`;
      }
      
      const newText = completeEmailContent.substring(0, start) + insertText + completeEmailContent.substring(end);
      setCompleteEmailContent(newText);
      
      // Set cursor position after the inserted shortcode
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + insertText.length, start + insertText.length);
      }, 0);
    }
    setShowShortcodes(false);
  };

  const generateEmailContent = (content: string) => {
    // Convert shortcodes to template variables for storage
    let processedContent = content
      .replace(/\[first_name\]/g, '{{customer_name}}')
      .replace(/\[customer_name\]/g, '{{customer_name}}')
      .replace(/\[email\]/g, '{{email}}')
      .replace(/\[phone\]/g, '{{phone}}')
      .replace(/\[track_type\]/g, '{{track_type}}')
      .replace(/\[booking_date\]/g, '{{booking_date}}')
      .replace(/\[booking_time\]/g, '{{booking_time}}')
      .replace(/\[duration\]/g, '{{duration}}')
      .replace(/\[booking_type\]/g, '{{booking_type}}')
      .replace(/\[total_amount\]/g, '{{total_amount}}')
      .replace(/\[deposit_amount\]/g, '{{deposit_amount}}')
      .replace(/\[special_requests\]/g, '{{special_requests}}')
      .replace(/\[driver_name\]/g, '{{driver_name}}')
      .replace(/\[event_name\]/g, '{{event_name}}')
      .replace(/\[event_date\]/g, '{{event_date}}')
      .replace(/\[registration_deadline\]/g, '{{registration_deadline}}')
      .replace(/\[category_name\]/g, '{{category_name}}')
      .replace(/\[vehicle_type\]/g, '{{vehicle_type}}')
      .replace(/\[registration_fee\]/g, '{{registration_fee}}')
      .replace(/\[first_place_prize\]/g, '{{first_place_prize}}')
      .replace(/\[second_place_prize\]/g, '{{second_place_prize}}')
      .replace(/\[wristbands_included\]/g, '{{wristbands_included}}')
      .replace(/\[tickets_included\]/g, '{{tickets_included}}')
      .replace(/\[time_range\]/g, '{{time_range}}');

    // Generate HTML content from the text content
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Jacky Circuit</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; background: white; padding: 30px; border-radius: 10px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
        .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #e11d48; }
        .logo { font-size: 28px; font-weight: bold; color: #e11d48; margin-bottom: 10px; }
        .content { white-space: pre-line; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">🏁 Jacky Circuit</div>
            <p>Professional Race Track</p>
        </div>
        <div class="content">${processedContent}</div>
    </div>
</body>
</html>`;

    return { htmlContent, textContent: processedContent };
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;

    try {
      const { htmlContent, textContent } = generateEmailContent(completeEmailContent);

      const { error } = await supabase
        .from('email_templates')
        .update({
          subject: editingTemplate.subject,
          html_content: htmlContent,
          text_content: textContent,
          is_active: editingTemplate.is_active
        })
        .eq('id', editingTemplate.id);

      if (error) throw error;

      toast.success('Email template saved successfully!');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error updating email template:', error);
      toast.error('Failed to save email template');
    }
  };

  const getTemplateDisplayName = (templateKey: string) => {
    switch (templateKey) {
      case 'track_booking_confirmation':
        return 'Track Booking Confirmation';
      case 'event_registration_confirmation':
        return 'Event Registration Confirmation';
      default:
        return templateKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getSampleData = (templateKey: string) => {
    switch (templateKey) {
      case 'track_booking_confirmation':
        return {
          customer_name: 'John Doe',
          track_type: 'Drag Strip',
          booking_date: '2025-08-15',
          booking_time: '10:00 AM - 2:00 PM',
          duration: 4,
          booking_type: 'Individual',
          traction_compound: true,
          special_requests: 'Need pit access',
          total_amount: 300,
          deposit_amount: 100
        };
      case 'event_registration_confirmation':
        return {
          customer_name: 'Jane Smith',
          driver_name: 'Jane Smith',
          event_name: 'Summer Championship 2025',
          event_date: '2025-08-20',
          registration_deadline: '2025-08-15',
          categories: [
            {
              name: 'Super Gas',
              vehicle_type: 'car',
              registration_fee: 150,
              wristbands_included: 4,
              tickets_included: 1,
              first_place_prize: 2250,
              second_place_prize: 1500,
              time_range: '10.20 – 9.90'
            },
            {
              name: 'Pro Street',
              vehicle_type: 'car',
              registration_fee: 200,
              wristbands_included: 6,
              tickets_included: 2,
              first_place_prize: 3000,
              second_place_prize: 2000,
              time_range: '9.50 – 8.90'
            }
          ],
          total_amount: 350
        };
      default:
        return {};
    }
  };

  const replaceVariables = (content: string, data: any): string => {
    let result = content;
    
    // Replace simple variables like {{variable_name}}
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, String(data[key] || ''));
    });

    // Handle conditional blocks like {{#if variable}}...{{/if}}
    result = result.replace(/{{#if\s+(\w+)}}(.*?){{\/if}}/gs, (match, variable, content) => {
      return data[variable] ? content : '';
    });

    // Handle loops like {{#each array}}...{{/each}}
    result = result.replace(/{{#each\s+(\w+)}}(.*?){{\/each}}/gs, (match, arrayName, template) => {
      const array = data[arrayName];
      if (!Array.isArray(array)) return '';
      
      return array.map(item => {
        let itemContent = template;
        Object.keys(item).forEach(key => {
          const regex = new RegExp(`{{${key}}}`, 'g');
          itemContent = itemContent.replace(regex, String(item[key] || ''));
        });
        return itemContent;
      }).join('');
    });

    return result;
  };

  const handlePreview = (template: EmailTemplate) => {
    const sampleData = getSampleData(template.template_key);
    
    // Convert the template's actual content to editable format for preview
    const editableContent = convertTemplateToEditableContent(template);
    
    // Create preview content using the template's actual content and sample data
    let previewContent = editableContent;
    
    // Replace shortcodes with sample data for preview
    previewContent = previewContent
      .replace(/\[first_name\]/g, sampleData.customer_name?.split(' ')[0] || 'John')
      .replace(/\[customer_name\]/g, sampleData.customer_name || 'John Doe')
      .replace(/\[email\]/g, 'customer@example.com')
      .replace(/\[phone\]/g, '+1234567890')
      .replace(/\[track_type\]/g, sampleData.track_type || 'Drag Strip')
      .replace(/\[booking_date\]/g, sampleData.booking_date || '2025-08-15')
      .replace(/\[booking_time\]/g, sampleData.booking_time || '10:00 AM - 2:00 PM')
      .replace(/\[duration\]/g, String(sampleData.duration || 4))
      .replace(/\[booking_type\]/g, sampleData.booking_type || 'Individual')
      .replace(/\[total_amount\]/g, String(sampleData.total_amount || 300))
      .replace(/\[deposit_amount\]/g, String(sampleData.deposit_amount || 100))
      .replace(/\[special_requests\]/g, sampleData.special_requests || 'None')
      .replace(/\[driver_name\]/g, sampleData.driver_name || 'Jane Smith')
      .replace(/\[event_name\]/g, sampleData.event_name || 'Summer Championship 2025')
      .replace(/\[event_date\]/g, sampleData.event_date || '2025-08-20')
      .replace(/\[registration_deadline\]/g, sampleData.registration_deadline || '2025-08-15')
      .replace(/\[category_name\]/g, 'Super Gas')
      .replace(/\[vehicle_type\]/g, 'car')
      .replace(/\[registration_fee\]/g, '150')
      .replace(/\[first_place_prize\]/g, '2250')
      .replace(/\[second_place_prize\]/g, '1500')
      .replace(/\[wristbands_included\]/g, '4')
      .replace(/\[tickets_included\]/g, '1')
      .replace(/\[time_range\]/g, '10.20 – 9.90');

    // Handle categories loop in preview
    if (sampleData.categories && Array.isArray(sampleData.categories)) {
      previewContent = replaceVariables(previewContent, sampleData);
    }
    
    setPreviewData({
      subject: template.subject,
      text_content: previewContent
    });
    setShowPreview(true);
  };

  // Group shortcodes by category
  const groupedShortcodes = (templateKey: string) => {
    const shortcodes = getShortcodes(templateKey);
    const grouped: { [key: string]: Shortcode[] } = {};
    
    shortcodes.forEach(shortcode => {
      if (!grouped[shortcode.category]) {
        grouped[shortcode.category] = [];
      }
      grouped[shortcode.category].push(shortcode);
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8">Email Management</h1>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'templates'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Email Templates
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'logs'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Email History
          </button>
        </div>

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {templates.map((template) => (
              <div key={template.id} className="bg-zinc-900 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {getTemplateDisplayName(template.template_key)}
                    </h3>
                    <p className="text-gray-400 text-sm">Key: {template.template_key}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        template.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePreview(template)}
                      className="text-gray-400 hover:text-blue-500 p-2"
                      title="Preview"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="text-gray-400 hover:text-white p-2"
                      title="Edit"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Subject</h4>
                    <p className="text-gray-400 text-sm bg-zinc-800 p-3 rounded">
                      {template.subject}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Variables</h4>
                    <div className="flex flex-wrap gap-1">
                      {template.variables.map((variable, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                        >
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Email Logs Tab */}
        {activeTab === 'logs' && (
          <div className="bg-zinc-900 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-800">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Template
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Sent At
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {emailLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-zinc-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {log.users?.full_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-400">
                            {log.recipient_email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {getTemplateDisplayName(log.template_key)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white max-w-xs truncate">
                          {log.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          log.status === 'sent' ? 'bg-green-100 text-green-800' : 
                          log.status === 'failed' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {log.status}
                        </span>
                        {log.error_message && (
                          <div className="text-xs text-red-400 mt-1">
                            {log.error_message}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Template Modal */}
        {editingTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit {getTemplateDisplayName(editingTemplate.template_key)}
                </h2>
                <button onClick={() => setEditingTemplate(null)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({
                      ...editingTemplate,
                      subject: e.target.value
                    })}
                    className="w-full border border-gray-300 rounded-md p-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your access is about to expire"
                  />
                </div>

                {/* Complete Email Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complete Email Content
                  </label>
                  <div className="relative border border-gray-300 rounded-md overflow-hidden">
                    {/* Line numbers container */}
                    <div className="absolute left-0 top-0 bottom-0 w-12 bg-gray-50 border-r border-gray-200 flex flex-col text-xs text-gray-500 font-mono select-none pointer-events-none z-10">
                      <div className="px-2 py-3 leading-6">
                        {completeEmailContent.split('\n').map((_, index) => (
                          <div key={index} className="h-6 flex items-center justify-end">
                            {index + 1}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Textarea */}
                    <textarea
                      id="complete-email-textarea"
                      value={completeEmailContent}
                      onChange={(e) => {
                        setCompleteEmailContent(e.target.value);
                        setCursorPosition(e.target.selectionStart);
                      }}
                      onSelect={(e) => setCursorPosition((e.target as HTMLTextAreaElement).selectionStart)}
                      rows={20}
                      className="w-full pl-16 pr-4 py-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none border-0 outline-none"
                      placeholder="JACKY CIRCUIT - CONFIRMATION&#10;============================&#10;&#10;Hello [customer_name],&#10;&#10;Thank you for your booking!&#10;&#10;Your booking details:&#10;- Track: [track_type]&#10;- Date: [booking_date]&#10;- Time: [booking_time]&#10;- Duration: [duration] hours&#10;- Total Amount: $[total_amount]&#10;&#10;PAYMENT INFORMATION:&#10;Please make payment to one of the following accounts:&#10;&#10;Curaçao Account:&#10;- Account No: 35160901&#10;- Bank: Banko Di Caribe&#10;- Name: Fundashon Autosport Curaçao&#10;&#10;Best regards,&#10;Jacky Circuit Team"
                      style={{ lineHeight: '1.5' }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    This is the complete email content that customers will receive. Edit everything including headers, payment info, and footer.
                  </p>
                </div>

                {/* Add Shortcode Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-sm font-medium text-gray-700">Add shortcode</span>
                    <div className="relative flex-1 max-w-xs">
                      <button
                        onClick={() => setShowShortcodes(!showShortcodes)}
                        className="w-full border border-gray-300 rounded-md px-4 py-2 text-left text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
                      >
                        <span>Select shortcode</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {showShortcodes && (
                        <div className="absolute top-full left-0 mt-1 w-96 bg-white border border-gray-300 rounded-md shadow-lg z-20 max-h-80 overflow-y-auto">
                          {Object.entries(groupedShortcodes(editingTemplate.template_key)).map(([category, shortcodes]) => (
                            <div key={category}>
                              <div className="px-4 py-2 bg-gray-100 text-sm font-semibold text-gray-700 border-b sticky top-0">
                                {category}
                              </div>
                              {shortcodes.map((shortcode) => (
                                <button
                                  key={shortcode.code}
                                  onClick={() => insertShortcode(shortcode.code)}
                                  className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                >
                                  <div className="font-mono text-sm text-blue-600 font-medium">{shortcode.code}</div>
                                  <div className="text-xs text-gray-600 mt-1">{shortcode.description}</div>
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        const textarea = document.getElementById('complete-email-textarea') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const selectedText = completeEmailContent.substring(start, end);
                          if (selectedText) {
                            const newText = completeEmailContent.substring(0, start) + `[${selectedText}]` + completeEmailContent.substring(end);
                            setCompleteEmailContent(newText);
                            setTimeout(() => {
                              textarea.focus();
                              textarea.setSelectionRange(start + selectedText.length + 2, start + selectedText.length + 2);
                            }, 0);
                          }
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Insert shortcode
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Click "Select shortcode" to choose from available variables, or select text and click "Insert shortcode" to wrap it in brackets.
                  </p>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={editingTemplate.is_active}
                    onChange={(e) => setEditingTemplate({
                      ...editingTemplate,
                      is_active: e.target.checked
                    })}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Active
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setEditingTemplate(null)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-md font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handlePreview(editingTemplate)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Preview
                  </button>
                  <button
                    onClick={handleSaveTemplate}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Save template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Email Preview</h2>
                <button onClick={() => setShowPreview(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Subject:</h3>
                  <p className="bg-gray-100 p-3 rounded text-gray-800 border">{previewData.subject}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Email Content:</h3>
                  <pre className="bg-gray-100 p-4 rounded text-gray-800 whitespace-pre-wrap text-sm border font-mono">
                    {previewData.text_content}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}